// pp-calc-buscador-cajas.js — ¿Qué Caja Necesito? Buscador por Medidas
// (/pages/que-caja-necesito)
//
// El cliente captura las medidas de SU producto (L×A×H en cm) y una holgura
// por lado; buscamos entre las cajas del catálogo (emitidas por la sección en
// un <script type="application/json" data-cajas-data>) cuáles le quedan.
//
// Las medidas de cada caja viven EN EL TÍTULO del producto ("9 x 9 x 32 cm -
// Cajas de Cartón para Envíos"). Títulos que no parsean (p. ej. cajas para
// pizza en pulgadas) se descartan en silencio.
//
// Una caja LE QUEDA si producto + holgura cabe en ALGUNA de las 6
// orientaciones (permutaciones). Ordenamos por volumen sobrante ascendente:
// la más justa primero — menos aire es menos relleno y menos peso
// volumétrico. Lógica pura probada en tests/calc-buscador-cajas.test.mjs.
(function (root) {
  'use strict';

  // Medidas en el título: "19 x 14 x 9 cm - …", con decimales opcionales.
  var REGEX_TITULO = /(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/i;

  var TOP_RESULTADOS = 10;
  var UMBRAL_JUSTA_PCT = 30;   // sobrante ≤ 30% del volumen necesario ⇒ "Justa"
  var UMBRAL_HOLGADA_PCT = 100; // 30–100% ⇒ "Holgada"; >100% solo si no hay mejores

  function num(x) {
    if (x === null || x === undefined || x === '') return null;
    var v = Number(String(x).replace(',', '.'));
    return isFinite(v) ? v : null;
  }

  // "9 x 9 x 32 cm - Cajas…" ⇒ { l: 9, a: 9, h: 32 } · sin medidas ⇒ null
  function parsearTitulo(titulo) {
    if (typeof titulo !== 'string') return null;
    var m = titulo.match(REGEX_TITULO);
    if (!m) return null;
    var l = Number(m[1]), a = Number(m[2]), h = Number(m[3]);
    if (!(l > 0 && a > 0 && h > 0)) return null;
    return { l: l, a: a, h: h };
  }

  // Filtra el catálogo crudo a cajas con medidas parseables; deduplica por
  // url (una caja puede vivir en más de una colección).
  function prepararCajas(lista) {
    if (!Array.isArray(lista)) return [];
    var vistas = {};
    var cajas = [];
    lista.forEach(function (p) {
      if (!p || typeof p.url !== 'string' || vistas[p.url]) return;
      var dims = parsearTitulo(p.title);
      if (!dims) return; // se descarta en silencio (p. ej. pizza en pulgadas)
      vistas[p.url] = true;
      cajas.push({
        titulo: p.title,
        url: p.url,
        precio: typeof p.precio === 'string' ? p.precio : '',
        disponible: p.disponible !== false,
        dims: dims
      });
    });
    return cajas;
  }

  // ¿Cabe [na, nb, nc] dentro de la caja en alguna de las 6 orientaciones?
  function cabeEnCaja(dimsCaja, necesarias) {
    var c = [dimsCaja.l, dimsCaja.a, dimsCaja.h];
    var n = necesarias;
    var perms = [
      [n[0], n[1], n[2]], [n[0], n[2], n[1]],
      [n[1], n[0], n[2]], [n[1], n[2], n[0]],
      [n[2], n[0], n[1]], [n[2], n[1], n[0]]
    ];
    for (var i = 0; i < perms.length; i++) {
      if (perms[i][0] <= c[0] && perms[i][1] <= c[1] && perms[i][2] <= c[2]) return true;
    }
    return false;
  }

  // Entradas: largoCm, anchoCm, altoCm del PRODUCTO, holguraCm POR LADO
  // (cada dimensión crece 2 × holgura: un lado y el otro), cajas (de
  // prepararCajas). Devuelve { resultados, total, huboSoloExcedidas } o null.
  function buscar(e) {
    if (!e) return null;
    var l = num(e.largoCm), a = num(e.anchoCm), h = num(e.altoCm), holgura = num(e.holguraCm);
    if ([l, a, h].some(function (v) { return v === null || v <= 0; })) return null;
    if (holgura === null || holgura < 0) return null;
    var cajas = Array.isArray(e.cajas) ? e.cajas : [];

    var necesarias = [l + 2 * holgura, a + 2 * holgura, h + 2 * holgura];
    var volNecesario = necesarias[0] * necesarias[1] * necesarias[2];

    var candidatas = [];
    cajas.forEach(function (caja) {
      if (!caja || !caja.dims) return;
      if (!cabeEnCaja(caja.dims, necesarias)) return;
      var volCaja = caja.dims.l * caja.dims.a * caja.dims.h;
      var sobranteCm3 = volCaja - volNecesario; // ≥ 0 si cabe
      var sobrantePct = (sobranteCm3 / volNecesario) * 100;
      candidatas.push({
        titulo: caja.titulo,
        url: caja.url,
        precio: caja.precio,
        disponible: caja.disponible,
        dims: caja.dims,
        sobranteLitros: sobranteCm3 / 1000,
        sobrantePct: sobrantePct,
        score: sobrantePct <= UMBRAL_JUSTA_PCT ? 'justa' : (sobrantePct <= UMBRAL_HOLGADA_PCT ? 'holgada' : 'excedida')
      });
    });

    candidatas.sort(function (x, y) { return x.sobranteLitros - y.sobranteLitros; });

    // Las que sobran más del 100% ni se muestran… salvo que no haya mejores.
    var buenas = candidatas.filter(function (c) { return c.score !== 'excedida'; });
    var pool = buenas.length > 0 ? buenas : candidatas;

    return {
      resultados: pool.slice(0, TOP_RESULTADOS),
      total: candidatas.length,
      huboSoloExcedidas: buenas.length === 0 && candidatas.length > 0,
      // La medida EXACTA que le pediríamos al cotizador de cajas a la medida:
      // el mismo producto+holgura que ya usó el motor para buscar. En modo
      // "Sé la medida de la caja" la holgura entra en 0, así que esto es
      // tal cual lo que el cliente tecleó — no se duplica esta aritmética
      // en el wiring de la banda (entregable 2), se reusa la de aquí.
      necesariasCm: { l: necesarias[0], a: necesarias[1], h: necesarias[2] }
    };
  }

  // ── La banda "Te la hacemos" (entregable 2) ───────────────────────────────
  // Funciones puras: no tocan el DOM ni la red, para poder probarlas contra
  // respuestas SIMULADAS del contrato de cajas-minimo sin desplegar nada.

  var NOMBRES_ESTILO = { '0201': 'RSC 0201' };

  // El copy SIEMPRE nombra el estilo (el número cambia con el estilo: medido
  // 1,297 piezas en 0201 vs 583–594 en 0427). Un código futuro desconocido se
  // nombra con su propio código en vez de inventarse un nombre.
  function nombreEstilo(codigo) {
    if (typeof codigo !== 'string' || !codigo) return null;
    return NOMBRES_ESTILO[codigo] || codigo;
  }

  // Redondea un mm a cm a 1 decimal. min ⇒ hacia arriba, max ⇒ hacia abajo:
  // así el rango que se ofrece nunca es más ancho que el rango real en mm
  // (el hueco de las pulgadas del brief: un /10 ingenuo es inocuo en cm,
  // pero se deja el redondeo hacia adentro de todas formas — si algún día
  // estos mm no fueran múltiplos limpios de 10, sigue siendo seguro).
  function convertirRangoMmACm(mm, direccion) {
    var v = num(mm);
    if (v === null) return null;
    var cm = v / 10;
    var factor = 10;
    return direccion === 'min' ? Math.ceil(cm * factor) / factor : Math.floor(cm * factor) / factor;
  }

  function formatearCm(n) {
    return String(Math.round(n * 10) / 10);
  }

  // La liga que NO hace retecleear: lleva las 3 medidas exactas al cotizador.
  // La URL la arma su único dueño en JS (assets/pp-liga-cotizador.js, que la
  // sección carga ANTES que este archivo); aquí sólo se decide qué medidas y
  // de dónde viene (utm_medium=buscador-medidas).
  function construirLigaCotizador(necesariasCm) {
    // TB-4: si pp-liga-cotizador.js no llegó, null (la banda usa la liga de
    // respaldo que arma el Liquid) — antes tronaba y se caía el buscador.
    if (!necesariasCm || !root.PPLigaCotizador || typeof root.PPLigaCotizador.armar !== 'function') return null;
    return root.PPLigaCotizador.armar({
      largo: formatearCm(necesariasCm.l),
      ancho: formatearCm(necesariasCm.a),
      alto: formatearCm(necesariasCm.h),
      medio: 'buscador-medidas'
    });
  }

  // Traduce la respuesta cruda de cajas-minimo (o su ausencia, si el
  // endpoint tardó, cayó o mandó algo raro) a lo que la banda puede pintar
  // SIN mentirle al cliente. Nunca deriva un eje de otro (piso_leido y
  // motivo son independientes) y nunca expone m2_piso/piso_leido — son
  // proxies del proveedor, no datos para el cliente.
  // Decisión a propósito: esta función NUNCA lee `resp.fabricable` directo.
  // `motivo` y `minimo_piezas` ya codifican lo mismo que fabricable
  // (true/false/null) de forma más específica — leer fabricable aparte
  // invitaría a alguien a tratarlo como un booleano de dos estados ("si
  // fabricable, di que sí se puede") y así se rompería la regla 2 (es
  // TRI-ESTADO) sin querer. Si algún día se necesita, hay que volver a leer
  // la regla 2 antes de tocar esto.
  function evaluarBanda(resp, necesariasCm) {
    var ctaHref = construirLigaCotizador(necesariasCm);
    if (!resp || typeof resp !== 'object') return { estado: 'generico', ctaHref: ctaHref };

    // Regla 3: fuera de rango no es un "no" pelón — se dice de cuánto a
    // cuánto SÍ se fabrica, si la respuesta trae el rango COMPLETO.
    if (resp.motivo === 'fuera_de_rango' && resp.rangos_mm && typeof resp.rangos_mm === 'object') {
      var r = resp.rangos_mm;
      var rango = {
        largo: { min: convertirRangoMmACm(r.largoMinMm, 'min'), max: convertirRangoMmACm(r.largoMaxMm, 'max') },
        ancho: { min: convertirRangoMmACm(r.anchoMinMm, 'min'), max: convertirRangoMmACm(r.anchoMaxMm, 'max') },
        alto: { min: convertirRangoMmACm(r.altoMinMm, 'min'), max: convertirRangoMmACm(r.altoMaxMm, 'max') }
      };
      // Un rangos_mm con un solo campo faltante (o no numérico) no se
      // enseña a medias: NO es lo mismo que rangos_mm venir null entero,
      // pero se trata igual — cae a "generico" a propósito, no por accidente
      // de que el .catch de más arriba lo atrape. Si algún día el fetch se
      // reorganiza y este `return` deja de estar bajo ese .catch, la regla
      // sigue puesta AQUÍ.
      //
      // `v > 0`, no sólo `isFinite`: num() (adentro de convertirRangoMmACm)
      // coacciona -0, [], "0" y 0 a cero, y cero ES finito — sin este
      // filtro, basura numérica del endpoint colaba "de 0 a 90 cm de
      // ancho". Es la misma regla de "nunca pintes lo que no puedes
      // prometer" que ya protege minimo_piezas, extendida a los 6 ejes.
      var rangoCompleto = [rango.largo.min, rango.largo.max, rango.ancho.min, rango.ancho.max, rango.alto.min, rango.alto.max]
        .every(function (v) { return typeof v === 'number' && isFinite(v) && v > 0; });
      if (rangoCompleto) {
        return { estado: 'rango', ctaHref: ctaHref, rango: rango };
      }
    }

    // Regla 1: minimo_piezas null significa "NO DIGAS MÍNIMO" — nunca 0.
    if (typeof resp.minimo_piezas === 'number' && isFinite(resp.minimo_piezas) && resp.minimo_piezas > 0) {
      return {
        estado: 'con_minimo',
        ctaHref: ctaHref,
        minimoPiezas: resp.minimo_piezas,
        estiloTexto: nombreEstilo(resp.estilo_codigo)
      };
    }

    // Regla 2: fabricable null (rangos no leídos, piso no leído, sin piso)
    // no es "sí" y no es "no" — invitación genérica, sin número ni promesa.
    return { estado: 'generico', ctaHref: ctaHref };
  }

  // ── El veredicto de resultados NO le miente al cliente en modo "caja" ────
  // En "sé la medida de la caja" no hay producto ("tu producto" es falso) ni
  // holgura elegida (está apagada, en 0) — mismo defecto que el estilo del
  // brief: un texto que describe algo que en ese modo no es cierto. Puras y
  // exportadas (no sólo texto en el wiring) para poder probarlas sin DOM;
  // el `i18n` es opcional — sin él, usan el mismo texto que ya vivía en el
  // JS (modo "producto") o su respaldo en español (modo "caja").
  function textoVeredictoTitulo(modo, n, i18n) {
    i18n = i18n || {};
    if (modo === 'caja') {
      if (n === 1) return i18n.veredicto_titulo_caja_una || 'Una caja de nuestro catálogo es parecida a esa medida';
      var muchas = i18n.veredicto_titulo_caja_muchas || '{{ n }} cajas de nuestro catálogo son parecidas a esa medida';
      return muchas.replace('{{ n }}', String(n));
    }
    return n === 1
      ? 'Una caja de nuestro catálogo le queda a tu producto'
      : n + ' cajas de nuestro catálogo le quedan a tu producto';
  }

  function textoVeredictoNota(modo, i18n) {
    i18n = i18n || {};
    if (modo === 'caja') {
      return i18n.nota_caja || 'Las medidas son las publicadas en el título de cada caja; la ficha de cada producto trae el detalle. El sobrante ya está calculado contra la medida que escribiste.';
    }
    return 'Las medidas son las publicadas en el título de cada caja; la ficha de cada producto trae el detalle. El sobrante ya descuenta tu producto con la holgura elegida.';
  }

  root.PPBuscadorCajas = {
    REGEX_TITULO: REGEX_TITULO,
    parsearTitulo: parsearTitulo,
    prepararCajas: prepararCajas,
    cabeEnCaja: cabeEnCaja,
    buscar: buscar,
    nombreEstilo: nombreEstilo,
    convertirRangoMmACm: convertirRangoMmACm,
    formatearCm: formatearCm,
    construirLigaCotizador: construirLigaCotizador,
    evaluarBanda: evaluarBanda,
    textoVeredictoTitulo: textoVeredictoTitulo,
    textoVeredictoNota: textoVeredictoNota
  };
})(typeof window !== 'undefined' ? window : globalThis);

// ── Wiring de la página (no corre en tests de node) ────────────────────────
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  function listo(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  listo(function () {
    var raiz = document.querySelector('[data-pp-calc-buscador-cajas]');
    if (!raiz) return;
    var B = window.PPBuscadorCajas;
    var $ = function (sel) { return raiz.querySelector(sel); };
    var form = $('form');
    var out = $('[data-calc-resultado]');
    var tplSinResultados = $('template[data-calc-sin-resultados]');

    var cajas = [];
    // Centavos y piezas del paquete por url (Fase 3): el precio POR PIEZA de
    // la caja del catálogo junto al de la exacta. Van aparte para no tocar
    // prepararCajas (el optimizador la copia 1:1 y su prueba carea la paridad).
    var paquetePorUrl = {};
    try {
      var nodo = $('script[data-cajas-data]');
      var crudo = JSON.parse(nodo ? nodo.textContent : '[]');
      cajas = B.prepararCajas(crudo);
      (Array.isArray(crudo) ? crudo : []).forEach(function (p) {
        if (p && typeof p.url === 'string') paquetePorUrl[p.url] = { centavos: p.precio_c, piezas: p.piezas };
      });
    } catch (_) { cajas = []; }

    // Una sola llave publicable, una sola vez en el archivo — la reusan la
    // telemetría y el fetch de la banda (mismo proyecto de Supabase).
    var SB_KEY = 'sb_publishable_48y6S7h2eQ-9uTF7pCNJNQ_Q0mz8VVX';

    function telemetria(inputs, resultado) {
      try {
        fetch('https://dhygbnysealeblqorjts.supabase.co/functions/v1/calc-telemetria', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', 'apikey': SB_KEY, 'Authorization': 'Bearer ' + SB_KEY },
          body: JSON.stringify({ calculadora: 'buscador_cajas', inputs: inputs, resultado: resultado })
        }).catch(function () {});
      } catch (_) { /* la telemetría jamás rompe el buscador */ }
    }

    // ── Textos de la banda: locales/ primero, respaldo en español si el
    //    bloque faltara o no parseara (mismo patrón que el explorador de
    //    cuenta — nunca pintar "undefined"). ─────────────────────────────────
    var I18N = {
      banda_titulo: '¿Ninguna es tu medida? Te la hacemos.',
      banda_cta: 'Cotizar mi medida',
      banda_texto_generico: 'Cajas personalizadas con tu medida exacta, precio al instante. Cotiza tu medida y te confirmamos los detalles.',
      banda_texto_minimo: 'Cajas personalizadas con tu medida exacta, precio al instante — desde {{ minimo }} piezas en caja {{ estilo }}.',
      banda_texto_minimo_sin_estilo: 'Cajas personalizadas con tu medida exacta, precio al instante — desde {{ minimo }} piezas.',
      banda_texto_rango: 'Esa medida exacta se nos sale de lo que fabricamos hoy — pero sí hacemos cajas de {{ largo_min }} a {{ largo_max }} cm de largo, {{ ancho_min }} a {{ ancho_max }} cm de ancho y {{ alto_min }} a {{ alto_max }} cm de alto. Cotiza tu medida y la ajustamos juntos.',
      eco_producto: 'Para tu producto de {{ medida }}, con holgura {{ holgura }}',
      eco_caja: 'Para tu caja de {{ medida }} (medida interior)',
      esperando: 'Buscando tu caja…',
      exacta_eyebrow: 'Tu caja exacta',
      exacta_sub: 'Medidas interiores · cartón corrugado kraft',
      exacta_precio: '{{ precio }}\u00a0c/u',
      exacta_iva: 'IVA incluido',
      exacta_minimo: 'Desde {{ n }} piezas',
      exacta_sin_aire: 'Sin aire de más: es justo tu medida',
      exacta_ahorro: '{{ kg }} kg menos de peso volumétrico por envío ({{ exacta }} contra {{ catalogo }} kg)',
      exacta_sola: 'Ninguna caja de línea le queda: ésta sí',
      exacta_cta: 'Cotízala',
      catalogo_eyebrow: 'La más justa del catálogo',
      catalogo_existencia: 'En existencia',
      catalogo_agotada: 'Agotada',
      catalogo_paquete: '{{ precio }} el paquete de {{ n }} · {{ unitario }}\u00a0c/u',
      catalogo_aire: 'Le sobra {{ pct }} % de aire ({{ litros }} L)',
      catalogo_peso: 'Peso volumétrico: {{ kg }} kg por envío',
      catalogo_cta: 'Ver caja',
      exacta_nota_peso: 'Peso volumétrico = largo × ancho × alto ÷ 5,000 (el divisor de Estafeta, FedEx y DHL), con las medidas publicadas de cada caja.'
    };
    try {
      var i18nEl = $('script[data-calc-i18n]');
      if (i18nEl) {
        var parsedI18n = JSON.parse(i18nEl.textContent);
        Object.keys(parsedI18n).forEach(function (k) { if (parsedI18n[k]) I18N[k] = parsedI18n[k]; });
      }
    } catch (_) { /* se quedan los respaldos */ }

    function fmt(n, dec) {
      return n.toLocaleString('es-MX', { maximumFractionDigits: dec === undefined ? 0 : dec });
    }

    function el(tag, clase, texto) {
      var e = document.createElement(tag);
      if (clase) e.className = clase;
      if (texto !== undefined) e.textContent = texto;
      return e;
    }

    // ── Interruptor «mido mi producto» / «sé la medida de la caja» ─────────
    // Mismo motor: buscar() no cambia. Sólo cambia CUÁL holgura entra — en
    // modo "caja" se apaga (0) y lo que se busca es la medida tal cual.
    var modoActual = 'producto';
    var holguraSel = $('#calc-buscador-holgura');
    var tabs = raiz.querySelectorAll('[data-pp-calc-modo]');

    function aplicarModo(modo) {
      modoActual = modo;
      tabs.forEach(function (btn) {
        var activo = btn.getAttribute('data-pp-calc-modo') === modo;
        btn.classList.toggle('is-active', activo);
        btn.setAttribute('aria-selected', String(activo));
      });
      raiz.querySelectorAll('[data-pp-calc-texto]').forEach(function (span) {
        span.hidden = span.getAttribute('data-pp-calc-texto') !== modo;
      });
      raiz.querySelectorAll('[data-pp-calc-ayuda]').forEach(function (span) {
        span.hidden = span.getAttribute('data-pp-calc-ayuda') !== modo;
      });
      raiz.querySelectorAll('[data-pp-calc-ayuda-holgura]').forEach(function (span) {
        span.hidden = span.getAttribute('data-pp-calc-ayuda-holgura') !== modo;
      });
      if (holguraSel) holguraSel.disabled = modo === 'caja';
      // Si ya había un resultado en pantalla, recalcula con el modo nuevo —
      // si no, el cliente ve un modo activo distinto pero un resultado que
      // sigue respondiendo al modo anterior.
      if (out && !out.hidden) correr(false);
    }

    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () { aplicarModo(btn.getAttribute('data-pp-calc-modo')); });
    });

    function correr(conTelemetria) {
      var f = new FormData(form);
      var datos = {
        largoCm: f.get('largo'), anchoCm: f.get('ancho'), altoCm: f.get('alto'),
        holguraCm: modoActual === 'caja' ? 0 : f.get('holgura'), cajas: cajas
      };
      var r = B.buscar(datos);
      // conTelemetria = búsqueda EXPLÍCITA (botón o medidas por la URL): el
      // precio exacto se pide ya; al teclear se espera al debounce.
      pintar(r, [Number(datos.largoCm), Number(datos.anchoCm), Number(datos.altoCm)], conTelemetria);
      if (conTelemetria && r) {
        telemetria(
          { largo_cm: Number(datos.largoCm) || null, ancho_cm: Number(datos.anchoCm) || null, alto_cm: Number(datos.altoCm) || null },
          { resultados: r.resultados.length }
        );
      }
    }

    // ── La banda "Te la hacemos" (entregable 2) ─────────────────────────────
    // Arriba de los resultados, siempre — nunca los sustituye ni desaparece
    // (ni sin resultados de catálogo, ni si el endpoint tarda o cae).

    function textoDesdeEstado(estado) {
      if (estado.estado === 'con_minimo') {
        var t = estado.estiloTexto ? I18N.banda_texto_minimo : I18N.banda_texto_minimo_sin_estilo;
        return t.replace('{{ minimo }}', fmt(estado.minimoPiezas)).replace('{{ estilo }}', estado.estiloTexto || '');
      }
      if (estado.estado === 'rango' && estado.rango) {
        return I18N.banda_texto_rango
          .replace('{{ largo_min }}', fmt(estado.rango.largo.min, 1)).replace('{{ largo_max }}', fmt(estado.rango.largo.max, 1))
          .replace('{{ ancho_min }}', fmt(estado.rango.ancho.min, 1)).replace('{{ ancho_max }}', fmt(estado.rango.ancho.max, 1))
          .replace('{{ alto_min }}', fmt(estado.rango.alto.min, 1)).replace('{{ alto_max }}', fmt(estado.rango.alto.max, 1));
      }
      return I18N.banda_texto_generico;
    }

    function construirBanda(necesariasCm) {
      var caja = el('div', 'pp-calc__banda');
      caja.appendChild(el('p', 'pp-calc__banda-titulo', I18N.banda_titulo));
      var texto = el('p', 'pp-calc__banda-texto', I18N.banda_texto_generico);
      texto.setAttribute('data-banda-texto', '');
      caja.appendChild(texto);
      var cta = document.createElement('a');
      cta.className = 'pp-calc__banda-cta';
      cta.textContent = I18N.banda_cta;
      var href = B.construirLigaCotizador(necesariasCm) || raiz.getAttribute('data-pp-liga-respaldo');
      if (href) cta.href = href;
      cta.setAttribute('data-banda-cta', '');
      caja.appendChild(cta);
      return caja;
    }

    function actualizarBanda(bandaEl, estado) {
      // Si ya se repintó el resultado (nueva búsqueda), esta banda vieja ya
      // no está en el DOM — no se toca, para no mutar algo que el cliente
      // ya no ve ni arriesgar una carrera con la banda nueva.
      if (!bandaEl || !bandaEl.isConnected) return;
      var textoEl = bandaEl.querySelector('[data-banda-texto]');
      if (textoEl) textoEl.textContent = textoDesdeEstado(estado);
      var ctaEl = bandaEl.querySelector('[data-banda-cta]');
      if (ctaEl && estado.ctaHref) ctaEl.href = estado.ctaHref;
    }

    var DEBOUNCE_BANDA_MS = 450;
    var timerBanda = null;

    // Debounce PROPIO del fetch a cajas-minimo, aparte del debounce de
    // recálculo en vivo (timerRecalc): el endpoint es público y sin JWT, así
    // que puede llamarse seguido — esto es lo que evita llamarlo a cada
    // tecla. Una caída, un timeout o una respuesta rara nunca rompen la
    // página: siempre resuelven en el estado "generico" de evaluarBanda.
    function programarBanda(necesariasCm, bandaEl) {
      clearTimeout(timerBanda);
      if (!necesariasCm) return;
      timerBanda = setTimeout(function () {
        pedirMinimo(necesariasCm, function (estado) { actualizarBanda(bandaEl, estado); });
      }, DEBOUNCE_BANDA_MS);
    }

    // cajas-minimo: el mínimo y, si la medida se sale, DE CUÁNTO A CUÁNTO sí
    // se fabrica (regla 3). Lo usa la banda de siempre y, desde la Fase 3,
    // «Tu caja exacta» cuando cajas-precio contesta 422 fuera_de_rango.
    function pedirMinimo(necesariasCm, alTerminar) {
      var url = 'https://dhygbnysealeblqorjts.supabase.co/functions/v1/cajas-minimo' +
        '?largo=' + necesariasCm.l + '&ancho=' + necesariasCm.a + '&alto=' + necesariasCm.h;
      // SÓLO `apikey`. cajas-minimo responde al preflight con
      // `Access-Control-Allow-Headers: apikey, content-type`; con un
      // `Authorization` de más, el NAVEGADOR bloquea la petición antes de
      // mandarla y la banda cae siempre al texto genérico. Pasó en
      // producción del #863 al 6-oct-2026 sin un solo error visible (el
      // .catch de abajo lo absorbe). Lo vigila
      // tests/calc-buscador-cajas-cors.test.mjs.
      fetch(url, { headers: { 'apikey': SB_KEY } })
        .then(function (resp) { return resp && resp.ok ? resp.json() : null; })
        .then(function (data) { alTerminar(B.evaluarBanda(data, necesariasCm)); })
        .catch(function () { alTerminar(B.evaluarBanda(null, necesariasCm)); });
    }

    // ── «Tu caja exacta» (Fase 3) ───────────────────────────────────────────
    // Lado a lado con la más justa del catálogo cuando ésa queda holgada (o
    // sola, si ninguna le queda). El precio NO bloquea: el par se pinta YA,
    // con un esqueleto del mismo alto donde va el precio, y el número llega
    // cuando conteste cajas-precio. Lógica pura en assets/pp-caja-exacta.js.
    //
    // TB-4 (§30, 24-oct-2026): se decide AL BUSCAR, no una vez al arrancar.
    // Antes `exactaActiva` se calculaba aquí y pedía que pp-caja-exacta.js y
    // window.PPLigaCotizador ya estuvieran; si llegaban en otro orden (o
    // tarde) se caía a la banda para toda la visita. Ahora cada búsqueda los
    // busca y, si faltan, ESPERA hasta ESPERA_PIEZAS_MS; si nunca llegan, la
    // banda de siempre y la raíz lo dice en data-pp-exacta-motivo.
    var EX = null;
    var EX_URL = raiz.getAttribute('data-pp-exacta-url') || '';
    var EX_LLAVE = raiz.getAttribute('data-pp-exacta-llave') || '';
    var hayLlavesExacta = !!(EX_URL && EX_LLAVE);
    var ESPERA_PIEZAS_MS = 3000;
    var PASO_ESPERA_MS = 100;
    function piezasExacta() {
      EX = window.PPCajaExacta || null;
      return !!(EX && window.PPLigaCotizador && typeof window.PPLigaCotizador.armar === 'function');
    }
    var timerPiezas = null;
    var cacheExacta = {};
    var timerExacta = null;
    var DEBOUNCE_EXACTA_MS = 450;

    function poner(plantilla, valores) {
      var t = String(plantilla);
      Object.keys(valores).forEach(function (k) { t = t.split('{{ ' + k + ' }}').join(valores[k]); });
      return t;
    }
    function mxn(n) {
      return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    function medidaTexto(d) {
      return [d.l, d.a, d.h].map(function (v) { return fmt(v, 1); }).join(' × ') + ' cm';
    }
    function ligaExacta(nec, cantidad) {
      return window.PPLigaCotizador.armar({
        largo: B.formatearCm(nec.l), ancho: B.formatearCm(nec.a), alto: B.formatearCm(nec.h),
        estilo: EX.ESTILO, cantidad: cantidad, acabado: EX.ACABADO, flauta: EX.FLAUTA,
        medio: 'buscador-medidas'
      });
    }

    function construirPar(r, dec) {
      var nec = r.necesariasCm;
      var par = el('div', 'pp-exacta-par pp-exacta-par--' + dec.modo);
      par.setAttribute('data-pp-exacta-par', '');

      var card = el('article', 'pp-exacta pp-exacta--exacta');
      card.setAttribute('data-pp-exacta', '');
      card.setAttribute('data-estado', 'cargando');
      card.setAttribute('aria-busy', 'true');
      var ceja = el('p', 'pp-exacta__eyebrow', I18N.exacta_eyebrow);
      ceja.setAttribute('data-pp-exacta-eyebrow', '');
      card.appendChild(ceja);
      card.appendChild(el('p', 'pp-exacta__medida', medidaTexto(nec)));
      card.appendChild(el('p', 'pp-exacta__sub', I18N.exacta_sub));

      // La zona del precio nace con su ALTO final (esqueleto en las mismas
      // dos líneas, mismo tamaño de letra): cuando llega el número no brinca.
      var zona = el('div', 'pp-exacta__zona');
      zona.setAttribute('data-pp-exacta-zona', '');
      var precio = el('p', 'pp-exacta__precio');
      precio.setAttribute('data-pp-exacta-precio', '');
      precio.appendChild(el('span', 'pp-exacta__esqueleto'));
      var minimo = el('p', 'pp-exacta__minimo');
      minimo.setAttribute('data-pp-exacta-minimo', '');
      minimo.appendChild(el('span', 'pp-exacta__esqueleto pp-exacta__esqueleto--corto'));
      zona.appendChild(precio);
      zona.appendChild(minimo);
      card.appendChild(zona);

      var ventajas = el('ul', 'pp-exacta__ventajas');
      ventajas.setAttribute('data-pp-exacta-ventajas', '');
      if (dec.modo === 'compara' && dec.comparacion) {
        var c = dec.comparacion;
        ventajas.appendChild(el('li', null, I18N.exacta_sin_aire));
        if (c.ahorroKg >= 0.05) {
          ventajas.appendChild(el('li', null, poner(I18N.exacta_ahorro, {
            kg: fmt(c.ahorroKg, 1), exacta: fmt(c.pesoVolExactaKg, 1), catalogo: fmt(c.pesoVolCatalogoKg, 1)
          })));
        }
      } else {
        ventajas.appendChild(el('li', null, I18N.exacta_sola));
      }
      card.appendChild(ventajas);

      var cta = document.createElement('a');
      cta.className = 'pp-exacta__cta';
      cta.setAttribute('data-pp-exacta-cta', '');
      cta.href = ligaExacta(nec, null);
      cta.textContent = I18N.exacta_cta;
      card.appendChild(cta);
      par.appendChild(card);

      if (dec.modo === 'compara' && r.resultados[0]) {
        par.appendChild(construirCatalogo(r.resultados[0], dec.comparacion));
      }
      return { par: par, card: card };
    }

    function construirCatalogo(caja, cmp) {
      var art = el('article', 'pp-exacta pp-exacta--catalogo');
      art.setAttribute('data-pp-exacta-catalogo', '');
      art.appendChild(el('p', 'pp-exacta__eyebrow', I18N.catalogo_eyebrow));
      var medida = el('p', 'pp-exacta__medida');
      var liga = document.createElement('a');
      liga.href = caja.url;
      liga.textContent = medidaTexto(caja.dims);
      medida.appendChild(liga);
      art.appendChild(medida);
      art.appendChild(el('p', 'pp-exacta__sub', caja.disponible ? I18N.catalogo_existencia : I18N.catalogo_agotada));
      var paq = paquetePorUrl[caja.url] || {};
      var unit = EX.porPieza(paq.centavos, paq.piezas);
      var precioTxt = unit !== null
        ? poner(I18N.catalogo_paquete, { precio: caja.precio, n: fmt(Math.floor(paq.piezas)), unitario: mxn(unit) })
        : caja.precio;
      var p = el('p', 'pp-exacta__precio-catalogo', precioTxt);
      p.setAttribute('data-pp-exacta-precio-catalogo', '');
      art.appendChild(p);
      var contras = el('ul', 'pp-exacta__ventajas pp-exacta__ventajas--contra');
      contras.appendChild(el('li', null, poner(I18N.catalogo_aire, { pct: fmt(cmp.airePct), litros: fmt(cmp.aireLitros, 1) })));
      contras.appendChild(el('li', null, poner(I18N.catalogo_peso, { kg: fmt(cmp.pesoVolCatalogoKg, 1) })));
      art.appendChild(contras);
      var ver = document.createElement('a');
      ver.className = 'pp-exacta__ver';
      ver.href = caja.url;
      ver.textContent = I18N.catalogo_cta;
      art.appendChild(ver);
      return art;
    }

    function exactaOk(card, res, nec) {
      if (!card || !card.isConnected) return;
      var precio = card.querySelector('[data-pp-exacta-precio]');
      var minimo = card.querySelector('[data-pp-exacta-minimo]');
      precio.textContent = '';
      precio.appendChild(document.createTextNode(poner(I18N.exacta_precio, { precio: mxn(res.unitarioConIva) }) + ' '));
      precio.appendChild(el('span', 'pp-exacta__iva', I18N.exacta_iva));
      minimo.textContent = poner(I18N.exacta_minimo, { n: fmt(res.minimoPiezas) });
      card.querySelector('[data-pp-exacta-cta]').href = ligaExacta(nec, res.minimoPiezas);
      card.setAttribute('data-estado', 'ok');
      card.removeAttribute('aria-busy');
    }

    // Con CUALQUIER error no se pinta la exacta: la tarjeta se vuelve la
    // banda de siempre («¿Ninguna es tu medida? Te la hacemos.» + texto +
    // «Cotízala» con las medidas). Guarda su alto para que lo de abajo no
    // suba de golpe.
    function exactaError(card, res, nec) {
      if (!card || !card.isConnected) return;
      card.style.minHeight = card.offsetHeight + 'px';
      card.setAttribute('data-estado', 'error');
      card.setAttribute('data-codigo', res.codigo || String(res.status || 'red'));
      card.removeAttribute('aria-busy');
      card.classList.add('pp-exacta--respaldo');
      card.querySelector('[data-pp-exacta-eyebrow]').textContent = I18N.banda_titulo;
      var zona = card.querySelector('[data-pp-exacta-zona]');
      zona.textContent = '';
      var texto = el('p', 'pp-exacta__respaldo', I18N.banda_texto_generico);
      texto.setAttribute('data-banda-texto', '');
      zona.appendChild(texto);
      var v = card.querySelector('[data-pp-exacta-ventajas]');
      if (v) v.remove();
      card.querySelector('[data-pp-exacta-cta]').href = ligaExacta(nec, null);
      // TB-4: ya sin precio, la tarjeta es la banda «¿Ninguna es tu medida?»,
      // y la banda NUNCA va antes del resultado: se muda al final (después de
      // la lista). Si en el par se queda la del catálogo, va sola.
      var par = out ? out.querySelector('[data-pp-exacta-par]') : null;
      if (par && par.querySelector('[data-pp-exacta-catalogo]')) {
        par.classList.toggle('pp-exacta-par--compara', false);
        par.classList.toggle('pp-exacta-par--solo-catalogo', true);
        out.appendChild(card);
      }
      // Fuera de rango: DE CUÁNTO A CUÁNTO sí se fabrica (regla 3 de la banda).
      if (res.status === 422 && res.codigo === 'fuera_de_rango') {
        pedirMinimo(nec, function (estado) {
          if (texto.isConnected) texto.textContent = textoDesdeEstado(estado);
        });
      }
    }

    function programarExacta(nec, card, inmediato) {
      clearTimeout(timerExacta);
      var k = EX.llave(nec);
      var lanzar = function () {
        var p = cacheExacta[k];
        if (!p) {
          p = EX.pedir(nec, { url: EX_URL, llave: EX_LLAVE });
          cacheExacta[k] = p;
          // Sólo se recuerda lo que no cambia en un minuto: el precio y el
          // «no se fabrica». Un 429/503/caída se vuelve a intentar.
          p.then(function (res) {
            if (!(res.estado === 'ok' || res.status === 422)) delete cacheExacta[k];
          });
        }
        p.then(function (res) {
          if (res.estado === 'ok') exactaOk(card, res, nec);
          else exactaError(card, res, nec);
        });
      };
      if (inmediato || cacheExacta[k]) lanzar();
      else timerExacta = setTimeout(lanzar, DEBOUNCE_EXACTA_MS);
    }

    // «Para tu producto de 49 × 40 × 11 cm, con holgura normal (+1 cm por
    // lado)» (TB-4): el resultado repite ARRIBA lo que puso el cliente. Es
    // el encabezado al que baja la pantalla al buscar.
    function textoHolgura() {
      var sel = form.querySelector('[name="holgura"]');
      var op = sel && sel.options && sel.selectedIndex >= 0 ? sel.options[sel.selectedIndex] : null;
      var txt = op && op.text ? String(op.text).trim() : '';
      if (!txt) return '+' + fmt(Number(sel ? sel.value : 1) || 1, 1) + ' cm por lado';
      return txt.charAt(0).toLowerCase() + txt.slice(1);
    }
    function construirEco(prodDims) {
      var medida = (prodDims || []).map(function (v) { return fmt(v, 1); }).join(' × ') + ' cm';
      var txt = modoActual === 'caja'
        ? poner(I18N.eco_caja, { medida: medida })
        : poner(I18N.eco_producto, { medida: medida, holgura: textoHolgura() });
      var eco = el('p', 'pp-calc__eco', txt);
      eco.setAttribute('data-pp-calc-eco', '');
      eco.setAttribute('tabindex', '-1');
      return eco;
    }

    function pintar(r, prodDims, inmediato, esperado) {
      if (!out) return;
      clearTimeout(timerPiezas);
      out.hidden = false;
      out.textContent = '';

      if (!r) {
        out.appendChild(el('p', 'pp-calc__error', 'Revisa los datos: largo, ancho y alto deben ser mayores a cero.'));
        return;
      }

      out.appendChild(construirEco(prodDims));

      // ¿Ya están las piezas de «Tu caja exacta»? Si faltan, se espera (con
      // la búsqueda en pantalla) y se vuelve a pintar; nunca se cae a la
      // banda sólo porque un archivo llegó después.
      esperado = esperado || 0;
      if (hayLlavesExacta && !piezasExacta()) {
        if (esperado < ESPERA_PIEZAS_MS) {
          out.appendChild(el('p', 'pp-calc__esperando', I18N.esperando));
          timerPiezas = setTimeout(function () { pintar(r, prodDims, inmediato, esperado + PASO_ESPERA_MS); }, PASO_ESPERA_MS);
          return;
        }
        raiz.setAttribute('data-pp-exacta-motivo', 'faltan-piezas');
      } else if (hayLlavesExacta) {
        raiz.removeAttribute('data-pp-exacta-motivo');
      }

      // La banda va PRIMERO — arriba de los resultados, completos abajo. No
      // los sustituye: "primero le ofrece lo que tenemos, y si quiere algo
      // muy específico, ya que se meta" (Padrino).
      //
      // Fase 3: si el catálogo le queda HOLGADO (o no le queda nada), en lugar
      // de la banda va «Tu caja exacta» con su precio en vivo, lado a lado con
      // la más justa del catálogo. Si el catálogo ya trae la medida (±½ cm
      // por lado), la banda de siempre: la de línea ES la exacta.
      //
      // TB-4: el orden es «Tu caja exacta», la más justa del catálogo y la
      // lista. La banda «¿Ninguna es tu medida?» NUNCA va antes del resultado:
      // cuando el catálogo ya trae la medida (cubierta) va al FINAL.
      var exactaLista = hayLlavesExacta && piezasExacta();
      var dec = exactaLista ? EX.decidir(r.necesariasCm, r.resultados[0]) : { modo: 'cubierta' };
      clearTimeout(timerExacta);
      var bandaAlFinal = null;
      if (dec.modo === 'cubierta') {
        bandaAlFinal = construirBanda(r.necesariasCm);
        programarBanda(r.necesariasCm, bandaAlFinal);
      } else {
        clearTimeout(timerBanda);
        var armado = construirPar(r, dec);
        out.appendChild(armado.par);
        if (dec.modo === 'compara') out.appendChild(el('p', 'pp-exacta__nota', I18N.exacta_nota_peso));
        programarExacta(r.necesariasCm, armado.card, inmediato);
      }

      if (r.resultados.length === 0) {
        if (tplSinResultados) out.appendChild(tplSinResultados.content.cloneNode(true));
        if (bandaAlFinal) out.appendChild(bandaAlFinal);
        return;
      }

      var veredicto = el('div', 'pp-calc__veredicto pp-calc__veredicto--ok');
      veredicto.appendChild(el('p', 'pp-calc__veredicto-titulo',
        B.textoVeredictoTitulo(modoActual, r.resultados.length, I18N)));
      veredicto.appendChild(el('p', 'pp-calc__veredicto-dato',
        r.huboSoloExcedidas
          ? 'Ojo: todas te quedan grandes — vas a necesitar relleno extra. Si envías seguido, considera una caja a la medida.'
          : 'Ordenadas de la más justa a la más holgada: menos aire es menos relleno y menos peso volumétrico.'));
      out.appendChild(veredicto);

      // Filas construidas con createElement/textContent — los títulos vienen
      // de NUESTRO catálogo (confiables), pero aquí nada se concatena en HTML.
      var dl = el('dl', 'pp-calc__detalle');
      r.resultados.forEach(function (c) {
        var fila = el('div', 'pp-calc__detalle-fila');

        var dt = document.createElement('dt');
        var liga = document.createElement('a');
        liga.href = c.url;
        liga.textContent = c.titulo;
        dt.appendChild(liga);
        dt.appendChild(document.createElement('br'));
        dt.appendChild(el('span', 'pp-calc__ayuda',
          c.dims.l + ' × ' + c.dims.a + ' × ' + c.dims.h + ' cm · sobran ' + fmt(c.sobranteLitros, 1) + ' L'));
        fila.appendChild(dt);

        var dd = document.createElement('dd');
        var chip;
        if (c.score === 'justa') chip = el('span', 'pp-calc__chip pp-calc__chip--ok', 'Justa');
        else if (c.score === 'holgada') chip = el('span', 'pp-calc__chip pp-calc__chip--alerta', 'Holgada');
        else chip = el('span', 'pp-calc__chip pp-calc__chip--gris', 'Muy Holgada');
        dd.appendChild(chip);
        dd.appendChild(document.createTextNode(' ' + c.precio + (c.disponible ? '' : ' · Agotada')));

        // "Ver cómo cabe": visor 3D girable de tu producto dentro de ESTA
        // caja (pedido de Padrino: herramientas visuales para darle vueltas).
        if (window.PPCaja3D && window.PPCaja3D.montar && prodDims && prodDims.every(function (v) { return v > 0; })) {
          dd.appendChild(document.createElement('br'));
          var toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'pp-c3d__toggle';
          toggle.textContent = 'Ver cómo cabe en 3D';
          toggle.setAttribute('aria-expanded', 'false');
          var panel = document.createElement('div');
          panel.setAttribute('data-c3d-panel', '');
          panel.hidden = true;
          toggle.addEventListener('click', function () {
            var abierto = !panel.hidden;
            panel.hidden = abierto;
            toggle.setAttribute('aria-expanded', String(!abierto));
            toggle.textContent = abierto ? 'Ver cómo cabe en 3D' : 'Ocultar el 3D';
            if (!abierto && !panel.dataset.montado) {
              window.PPCaja3D.montar(panel, {
                caja: [c.dims.l, c.dims.a, c.dims.h],
                producto: prodDims,
                etiqueta: 'Tu producto dentro de la caja — arrástralo para girarlo'
              });
              panel.dataset.montado = '1';
            }
          });
          dd.appendChild(toggle);
          fila.appendChild(dd);
          fila.appendChild(panel);
        } else {
          fila.appendChild(dd);
        }

        dl.appendChild(fila);
      });
      out.appendChild(dl);

      out.appendChild(el('p', 'pp-calc__nota', B.textoVeredictoNota(modoActual, I18N)));
      if (bandaAlFinal) out.appendChild(bandaAlFinal);
    }

    // Al buscar, la pantalla baja al encabezado del resultado («Para tu
    // producto de…»), también en celular; el scroll-margin del CSS deja
    // libre el encabezado fijo de la tienda.
    function bajarAlResultado(suave) {
      var eco = out && out.querySelector('[data-pp-calc-eco]');
      var destino = eco || out;
      if (destino && destino.scrollIntoView) destino.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' });
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      correr(true);
      bajarAlResultado(true);
    });

    // Medidas por la URL (14-oct-2026): la búsqueda de la tienda manda aquí
    // «35 x 25 x 20» con ?modo=caja&largo=&ancho=&alto=, y el buscador las
    // compara solo. Sólo se corre con las TRES medidas numéricas > 0.
    (function desdeLaUrl() {
      var p;
      try { p = new URLSearchParams(window.location.search); } catch (_) { return; }
      var l = Number(p.get('largo')), a = Number(p.get('ancho')), h = Number(p.get('alto'));
      if (![l, a, h].every(function (v) { return isFinite(v) && v > 0; })) return;
      var modo = p.get('modo') === 'producto' ? 'producto' : (p.get('modo') === 'caja' ? 'caja' : null);
      if (modo) aplicarModo(modo);
      ['largo', 'ancho', 'alto'].forEach(function (k, i) {
        var input = form.querySelector('[name="' + k + '"]');
        if (input) input.value = String([l, a, h][i]);
      });
      correr(true);
      bajarAlResultado(false);
    })();

    var timerRecalc = null;
    function recalcEnVivo() {
      if (!out || out.hidden) return;
      clearTimeout(timerRecalc);
      timerRecalc = setTimeout(function () { correr(false); }, 350);
    }
    form.addEventListener('input', recalcEnVivo);
    form.addEventListener('change', recalcEnVivo);
  });
})();
